/**
 * Extract the first page of a PDF as a JPEG cover image using PDF.js.
 * Returns a File ready for upload to Blossom.
 *
 * The worker is loaded via a blob URL created from a fetch of the CDN script.
 * This avoids CSP issues with external worker-src by routing through blob:.
 */

import * as pdfjsLib from 'pdfjs-dist';

let workerReady = false;

/**
 * Initialize the PDF.js worker the first time it is needed.
 * Creates a blob URL from the CDN worker script so that both
 * worker-src blob: and script-src blob: permit it.
 */
async function ensureWorker(): Promise<void> {
  if (workerReady) return;

  const workerUrl = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;

  try {
    // Fetch the worker script and re-serve it as a same-origin blob
    const res = await fetch(workerUrl);
    if (!res.ok) throw new Error(`Failed to fetch PDF worker: ${res.status}`);
    const text = await res.text();
    const blob = new Blob([text], { type: 'application/javascript' });
    pdfjsLib.GlobalWorkerOptions.workerSrc = URL.createObjectURL(blob);
  } catch {
    // Fallback: use the CDN URL directly and hope CSP allows it
    pdfjsLib.GlobalWorkerOptions.workerSrc = workerUrl;
  }

  workerReady = true;
}

/**
 * Render the first page of a PDF File to a canvas and return it as a JPEG File.
 * @param pdf - The PDF File object
 * @param scale - Render scale (default 1.5 — gives ~1200px wide for A4)
 * @returns A File with type "image/jpeg" named "cover.jpg"
 */
export async function extractPdfCover(pdf: File, scale = 1.5): Promise<File> {
  await ensureWorker();

  const arrayBuffer = await pdf.arrayBuffer();
  const doc = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
  const page = await doc.getPage(1);

  const viewport = page.getViewport({ scale });
  const canvas = document.createElement('canvas');
  canvas.width = viewport.width;
  canvas.height = viewport.height;

  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not get 2D canvas context');
  await page.render({ canvasContext: ctx, viewport }).promise;

  return new Promise<File>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) { reject(new Error('canvas.toBlob returned null — PDF render may have failed')); return; }
        resolve(new File([blob], 'cover.jpg', { type: 'image/jpeg' }));
      },
      'image/jpeg',
      0.85,
    );
  });
}
